import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { MessageSquare, Plus, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  open: "bg-amber-500/15 text-amber-700 border-amber-300",
  acknowledged: "bg-blue-500/15 text-blue-700 border-blue-300",
  resolved: "bg-emerald-500/15 text-emerald-700 border-emerald-300",
  archived: "bg-slate-500/15 text-slate-700 border-slate-300",
};

export default function PortalCommunications() {
  const [requests, setRequests] = useState([]);
  const [types, setTypes] = useState([]);
  const [channels, setChannels] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ request_type_key: "", subject: "", body: "" });

  const load = async () => {
    try {
      const [r, t, c] = await Promise.all([
        axios.get(`${API}/comm/requests`),
        axios.get(`${API}/comm/request-types`),
        axios.get(`${API}/comm/channels`),
      ]);
      setRequests(r.data.requests || []);
      setTypes(t.data.types || []);
      setChannels(c.data.channels || []);
    } catch (e) {
      toast.error("Failed to load");
    }
  };

  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.request_type_key) return toast.error("Please pick a request type");
    if (!form.subject.trim()) return toast.error("Subject required");
    try {
      await axios.post(`${API}/comm/requests`, form);
      toast.success("Request submitted");
      setOpen(false);
      setForm({ request_type_key: "", subject: "", body: "" });
      load();
    } catch (e) {
      toast.error("Failed to submit");
    }
  };

  const toggleChannel = async (ch) => {
    try {
      await axios.put(`${API}/comm/channels`, { channel: ch.channel, is_enabled: !ch.is_enabled });
      load();
    } catch (e) {
      toast.error("Failed to update channel");
    }
  };

  return (
    <div className="space-y-6 p-6" data-testid="portal-communications">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2"><MessageSquare className="h-7 w-7 text-emerald-600" /> Communications</h1>
          <p className="text-muted-foreground mt-1">Submit requests to the office (sick day, time-off, billing, etc.) and track responses.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="new-request-btn"><Plus className="h-4 w-4 mr-1" /> New Request</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New Request</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground">Type</label>
                <Select value={form.request_type_key} onValueChange={(v) => setForm({ ...form, request_type_key: v })}>
                  <SelectTrigger data-testid="request-type-select"><SelectValue placeholder="Choose a request type" /></SelectTrigger>
                  <SelectContent>
                    {types.map((t) => (<SelectItem key={t.id} value={t.key}>{t.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Subject</label>
                <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="request-subject-input" />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Message</label>
                <Textarea rows={5} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="request-body-input" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={submit} className="bg-emerald-600 hover:bg-emerald-700" data-testid="submit-request-btn"><Send className="h-4 w-4 mr-1" /> Submit</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Notification Channels</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="channel-prefs">
          {channels.map((ch) => (
            <div key={ch.channel} className="flex items-center justify-between p-3 border rounded-md">
              <div>
                <p className="font-medium capitalize">{ch.channel.replace("_", " ")}</p>
                <p className="text-xs text-muted-foreground">{ch.is_enabled ? "You'll receive updates here" : "Off — won't notify"}</p>
              </div>
              <Switch checked={ch.is_enabled} onCheckedChange={() => toggleChannel(ch)} data-testid={`channel-toggle-${ch.channel}`} />
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">My Requests</CardTitle></CardHeader>
        <CardContent>
          {requests.length === 0 ? (
            <p className="text-sm text-muted-foreground italic" data-testid="no-requests">No requests yet.</p>
          ) : (
            <div className="space-y-2" data-testid="my-requests-list">
              {requests.map((r) => (
                <div key={r.id} className="p-3 border rounded-md">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-medium text-sm">{r.subject}</p>
                      <p className="text-xs text-muted-foreground">{r.request_type_key} • {new Date(r.created_at).toLocaleString()}</p>
                      {r.notes && <p className="text-xs mt-2 italic">Reply: {r.notes}</p>}
                    </div>
                    <Badge variant="outline" className={STATUS_COLORS[r.status]}>{r.status}</Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
